/**
 * CR-8720: the customer's answer to the supplier's counter-proposed
 * collection date. Shown on the Collection tab while the process is at
 * 'date_proposed': accept the supplier's date (locks it, -> confirmed) or
 * propose another (-> pending, the supplier's turn again). Nothing is locked
 * until one side accepts.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { CalendarClock, CheckCircle2 } from 'lucide-react';
import { isPastDate } from './claimLimits';
import { todayLocalISO } from './localDate';

export interface CollectionDatePanelProps {
  /** The date the customer proposed (formatted by the parent). */
  proposedDate: string;
  /** The date the supplier proposed instead (formatted by the parent). */
  supplierDate: string;
  saving: boolean;
  /** Accept the supplier's date: posts status 'confirmed'. */
  onAccept: () => void;
  /** Propose a different date: posts status 'pending' with the new date. */
  onCounter: (proposedCollectionDate: string, customerNotes: string) => void;
}

/**
 * Accept the supplier's collection date, or propose another one (today or later).
 */
export function CollectionDatePanel({ proposedDate, supplierDate, saving, onAccept, onCounter }: CollectionDatePanelProps) {
  const [counter, setCounter] = useState({ proposed_collection_date: '', customer_notes: '' });
  const [dateError, setDateError] = useState<string | null>(null);
  const today = todayLocalISO();

  const handleCounter = () => {
    if (!counter.proposed_collection_date) {
      setDateError('Enter the date you are proposing.');
      return;
    }
    if (isPastDate(counter.proposed_collection_date, today)) {
      setDateError('Proposed collection date can’t be in the past.');
      return;
    }
    setDateError(null);
    onCounter(counter.proposed_collection_date, counter.customer_notes);
  };

  return (
    <div className="border-t pt-4 space-y-4" data-testid="collection-date-panel">
      <h4 className="font-medium text-sm">Supplier proposed a different date</h4>
      <p className="text-sm text-muted-foreground">
        You proposed <span className="font-medium text-foreground">{proposedDate}</span>; the supplier proposed{' '}
        <span className="font-medium text-foreground">{supplierDate}</span>. Accept it, or propose another date.
        The date is only locked in once one side accepts.
      </p>
      <Button onClick={onAccept} disabled={saving} className="bg-green-600 hover:bg-green-700">
        <CheckCircle2 className="h-4 w-4 mr-2" /> Accept {supplierDate}
      </Button>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="counter_collection_date">Propose another date</Label>
          <Input
            id="counter_collection_date"
            type="date"
            min={today}
            value={counter.proposed_collection_date}
            aria-invalid={!!dateError}
            onChange={e => {
              const v = e.target.value;
              setCounter(c => ({ ...c, proposed_collection_date: v }));
              setDateError(isPastDate(v, today) ? 'Proposed collection date can’t be in the past.' : null);
            }}
          />
          {dateError && <p className="text-sm text-red-500" role="alert">{dateError}</p>}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="counter_collection_notes">Notes</Label>
        <Textarea
          id="counter_collection_notes"
          rows={2}
          value={counter.customer_notes}
          placeholder="Why this date..."
          onChange={e => setCounter(c => ({ ...c, customer_notes: e.target.value }))}
        />
      </div>
      <Button variant="outline" onClick={handleCounter} disabled={saving || !counter.proposed_collection_date || !!dateError}>
        <CalendarClock className="h-4 w-4 mr-2" /> Propose this date
      </Button>
    </div>
  );
}
